// src/components/hooks/useHubData.js
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../supabaseClient";
import fetchMarketplaceListings from "../../services/marketplace/fetchMarketplaceListings";
import { resolveImageUrl } from "../../services/mediaUrl";

const PAGE_SIZE = 12;

export default function useHubData(currentUser) {
  const [forSale, setForSale] = useState([]);
  const [forSaleCursor, setForSaleCursor] = useState(null);
  const [forSaleHasMore, setForSaleHasMore] = useState(true);
  const [forSaleLoading, setForSaleLoading] = useState(false);

  const [publicFeed, setPublicFeed] = useState([]);
  const [publicCursor, setPublicCursor] = useState(null);
  const [publicHasMore, setPublicHasMore] = useState(true);
  const [publicLoading, setPublicLoading] = useState(false);

  const [myFeed, setMyFeed] = useState([]);
  const [socialFeed, setSocialFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  const [connections, setConnections] = useState([]);

  // --- Helpers ---
  const getImageUrl = (item) => {
    if (!item) return "/placeholder.png";

    const image =
      item.item_images?.[0] ||
      item.user_items?.item_images?.[0] ||
      null;

    return resolveImageUrl(image) || "/placeholder.png";
  };

  const getDisplayName = (item) => {
    if (!item) return "Unnamed Item";

    const userItem = item.user_items || item;
    const canonical = userItem?.items || item.items || null;
    const hasCanonicalItem = Boolean(userItem?.item_id && canonical);

    return hasCanonicalItem
      ? canonical?.name || "Unnamed Item"
      : userItem?.user_specific_name || canonical?.name || "Unnamed Item";
  };

  // --- Fetch For Sale (Marketplace V2 unified read) ---
  const fetchForSale = useCallback(async () => {
    if (!forSaleHasMore || forSaleLoading) return;

    setForSaleLoading(true);

    try {
      const offset = Number(forSaleCursor || 0);

      const data = await fetchMarketplaceListings({
        offset,
        limit: PAGE_SIZE,
      });

      if (data.length) {
        setForSale((prev) => {
          const existingIds = new Set(
            prev.map((item) => item.listingId)
          );

          const newItems = data.filter(
            (item) => !existingIds.has(item.listingId)
          );

          return [...prev, ...newItems];
        });

        setForSaleCursor(offset + data.length);
      }

      if (data.length < PAGE_SIZE) {
        setForSaleHasMore(false);
      }
    } catch (err) {
      console.error(
        "Unexpected error loading marketplace listings:",
        err
      );

      setForSaleHasMore(false);
    } finally {
      setForSaleLoading(false);
    }
  }, [
    forSaleCursor,
    forSaleHasMore,
    forSaleLoading,
  ]);

  // --- Fetch Public Feed ---
  const fetchPublicFeed = useCallback(async () => {
    if (!publicHasMore || publicLoading) return;

    setPublicLoading(true);

    try {
      let query = supabase
        .from("user_items")
        .select(`
          id,
          item_id,
          user_id,

          user_specific_name,
          user_specific_brand,
          user_specific_set_name,
          user_specific_year,

          condition,
          is_private,
          created_at,

          items (
            id,
            name,
            brand,
            set_name,
            year
          ),

          item_images!fk_user_item_image (
            id,
            storage_provider,
            storage_bucket,
            object_key,
            position
          )
        `)
        .eq("is_private", false)
        .limit(PAGE_SIZE)
        .order("created_at", { ascending: false });

      // Cursor pagination
      if (publicCursor) {
        query = query.lt("created_at", publicCursor);
      }

      const { data, error } = await query;

      if (error) {
        console.error(
          "Error loading public feed:",
          error
        );

        setPublicHasMore(false);

      } else if (data?.length) {

        // -----------------------------------
        // Fetch usernames separately
        // -----------------------------------

        const userIds = [
          ...new Set(
            data
              .map(item => item.user_id)
              .filter(Boolean)
          )
        ];

       const {
          data: usersData,
          error: usersError,
        } = await supabase.rpc("get_user_profiles", {
          p_user_ids: userIds,
        });

          if (usersError) {
          console.error(
            "Users fetch error:",
            usersError
          );
}

        // Create lookup map
        const userMap = {};

        usersData?.forEach(user => {
          userMap[user.id] = user;
        });

        // Attach user data to each item
        const enrichedData = data.map(item => ({
          ...item,
          users: userMap[item.user_id] || null,
        }));

        setPublicFeed(prev => {
          const existingIds = new Set(prev.map(i => i.id));

          const newItems = enrichedData.filter(
            i => !existingIds.has(i.id)
          );

          return [...prev, ...newItems];
        });

        setPublicCursor(
          data[data.length - 1].created_at
        );

        if (data.length < PAGE_SIZE) {
          setPublicHasMore(false);
        }
      }

    } catch (err) {

      console.error(
        "Unexpected error loading public feed:",
        err
      );

      setPublicHasMore(false);

    }

    setPublicLoading(false);

  }, [
    publicCursor,
    publicHasMore,
  ]);

  const initialLoadRef = useRef(false);

  // --- Initial load ---
  useEffect(() => {
    if (initialLoadRef.current) return;

    initialLoadRef.current = true;

    const loadInitial = async () => {
      setLoading(true);

      await Promise.all([
        fetchForSale(),
        fetchPublicFeed(),
        fetchConnections(),
      ]);

      setLoading(false);
    };

    loadInitial();
  }, []);

  // --- Fetch Connections ---
const fetchConnections = useCallback(async () => {
  if (!currentUser?.id) return;

  try {
    // -----------------------------------
    // GET ACCEPTED CONNECTIONS
    // -----------------------------------
    const { data: connectionData, error } = await supabase
      .from("user_connections")
      .select(`
        requester_id,
        receiver_id,
        status
      `)
      .eq("status", "accepted")
      .or(
        `requester_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`
      );

    if (error) {
      console.error(
        "Error loading connections:",
        error
      );
      return;
    }

    // -----------------------------------
    // GET THE OTHER USER ID
    // -----------------------------------
    const connectedUserIds = [
      ...new Set(
        (connectionData || []).map(conn =>
          conn.requester_id === currentUser.id
            ? conn.receiver_id
            : conn.requester_id
        )
      )
    ];

    if (!connectedUserIds.length) {
      setConnections([]);
      return;
    }

    // -----------------------------------
    // FETCH USER DATA
    // -----------------------------------
    const {
      data: usersData,
      error: usersError,
    } = await supabase.rpc("get_user_profiles", {
      p_user_ids: connectedUserIds,
    });

    if (usersError) {
      console.error(
        "Error loading connection users:",
        usersError
      );
      return;
    }

    setConnections(usersData || []);

  } catch (err) {

    console.error(
      "Unexpected connection fetch error:",
      err
    );

  }
}, [currentUser]);

  return {
    forSale,
    fetchForSale,
    forSaleHasMore,
    forSaleLoading,
    publicFeed,
    fetchPublicFeed,
    publicHasMore,
    publicLoading,
    myFeed,
    socialFeed,
    connections,
    loading,
    getImageUrl,
    getDisplayName,
  };
}
